import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router';
import { 
  Zap, Sparkles, ChevronRight, ChevronLeft, 
  RotateCcw, ShieldCheck, Target, Layers, Search,
  CheckCircle, Loader2, Brain, Info, Database,
  Printer, Filter, GraduationCap, XCircle, Clock,
  Trophy, Medal, Activity, Check, BookOpen,
  ArrowRight, LayoutGrid, ListFilter, Keyboard, 
  Download, X, RefreshCw, Undo2, HelpCircle,
  Globe, Bookmark, Timer, AlertCircle, Cloud,
  Save, FastForward, Play, Pause, BarChart3, Binary,
  Lightbulb, Gauge, History, Lock, Star, Volume2,
  CheckSquare, ArrowUpRight, Flame, Award, Heart
} from 'lucide-react';
import { Flashcard, FlashcardSRSMetadata, FlashcardSession } from '../types';
import { StorageService } from '../services/storageService';
import { useAuth } from '../components/AuthBridge';
import { GamificationService } from '../services/gamificationService';
import { ModuleHeader } from '../components/ModuleHeader';
import { PageLoader } from '../components/PageLoader';
import { AdIntervalBreak } from '../components/AdIntervalBreak';
import { AdUnit } from '../components/AdUnit';
import { SponsoredService } from '../services/sponsoredService';
import { GuestTrialService } from '../services/guestTrialService';
import { GuestLimitModal } from '../components/GuestLimitModal';
import { useThemeTokens } from '../hooks/useActiveTheme';

type FlashcardFilter = 'all' | 'due' | 'new' | 'mastered' | 'starred' | 'review_later';

interface MatchItem {
    id: string; // card id
    text: string;
    type: 'front' | 'back';
}

export const Flashcards: React.FC = () => {
    const { certId } = useParams<{ certId: string }>();
    const navigate = useNavigate();
    const { activeCert, user, updateUser, isLoading: authLoading } = useAuth();
    const [config, setConfig] = useState(SponsoredService.getConfig());
    const tokens = useThemeTokens();


    useEffect(() => {
        const handleUpdate = () => {
            setConfig(SponsoredService.getConfig());
        };
        window.addEventListener('sponsored-config-updated', handleUpdate);
        return () => window.removeEventListener('sponsored-config-updated', handleUpdate);
    }, []);
    
    // Core data and loading structures
    const [cards, setCards] = useState<Flashcard[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    
    // Quizlet Layout Mode switches
    const [viewMode, setViewMode] = useState<'dashboard' | 'study'>('dashboard');
    const [studyType, setStudyType] = useState<'flashcards' | 'match'>('flashcards');
    
    // Filtering states
    const [filter, setFilter] = useState<FlashcardFilter>('all');
    const [selectedCategory, setSelectedCategory] = useState<string>('All');
    const [searchTerm, setSearchTerm] = useState('');
    const [isCelebrating, setIsCelebrating] = useState(false);
    const [showResumeModal, setShowResumeModal] = useState(false);
    const [showHint, setShowHint] = useState(false);
    const [sessionXp, setSessionXp] = useState(0);
    const [isSessionComplete, setIsSessionComplete] = useState(false);
    const [showAdBreak, setShowAdBreak] = useState(false);
    const [lastAdIndex, setLastAdIndex] = useState(-1);
    const [adRefreshKey, setAdRefreshKey] = useState(0);
    const [masteredInSession, setMasteredInSession] = useState(0);

    // Guest trial exploration tracking
    const [showGuestLimitModal, setShowGuestLimitModal] = useState(false);
    const isGuest = !user;
    const guestLimit = config.guestQuestionLimit || 50;
    const [guestState, setGuestState] = useState(() => GuestTrialService.getState());

    useEffect(() => {
        const handleGuestUpdate = (e: any) => {
            if (e?.detail) setGuestState(e.detail);
            else setGuestState(GuestTrialService.getState());
        };
        window.addEventListener('guest-trial-updated', handleGuestUpdate);
        return () => window.removeEventListener('guest-trial-updated', handleGuestUpdate);
    }, []);

    // Advanced Quizlet-style features
    const [isPlaying, setIsPlaying] = useState(false); // Slideshow Auto Play
    const [autoplaySpeed, setAutoplaySpeed] = useState<3000 | 5000 | 8000>(5000); // ms speed
    const [starredIds, setStarredIds] = useState<string[]>(() => {
        try {
            const saved = localStorage.getItem(`starred_flashcards_${certId || 'cna'}`);
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });

    // Match Mode states
    const [matchPairs, setMatchPairs] = useState<MatchItem[]>([]);
    const [selectedPairItem, setSelectedPairItem] = useState<MatchItem | null>(null);
    const [matchedIds, setMatchedIds] = useState<string[]>([]);
    const [matchTimer, setMatchTimer] = useState<number>(0);
    const [matchTimerActive, setMatchTimerActive] = useState<boolean>(false);
    const [matchBestScore, setMatchBestScore] = useState<number>(() => {
        try {
            const saved = localStorage.getItem(`match_best_time_${certId || 'cna'}`);
            return saved ? parseInt(saved, 10) : 0;
        } catch {
            return 0;
        }
    });

    const srsMap = useMemo(() => user?.flashcardSRS || {}, [user?.flashcardSRS]);

    // Load initial flashcard sets
    useEffect(() => {
        const load = async () => {
            if (authLoading) return;
            setIsLoading(true);
            try {
                const data = await StorageService.getFlashcards(certId || 'cna', { limit: 500 });
                const fetchedCards = data.items || [];
                setCards(fetchedCards);
                
                const session = user?.flashcardSession;
                if (session && session.certId === (certId || 'cna')) {
                    if (session.currentIndex > 0 && session.currentIndex < fetchedCards.length) {
                        setShowResumeModal(true);
                    }
                }
            } catch (e) {
                console.error("Registry Ingestion Failure:", e);
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [certId, authLoading, user?.id]);

    useEffect(() => {
        if (viewMode === 'study') {
            window.dispatchEvent(
                new CustomEvent('update-breadcrumbs-nesting', {
                    detail: {
                        currentPageKey: 'flashcards',
                        level2Label: `Study Set: ${selectedCategory || 'All'}`,
                        onBackAction: 'flashcards-back-action',
                    },
                })
            );
        } else {
            window.dispatchEvent(
                new CustomEvent('update-breadcrumbs-nesting', {
                    detail: null,
                })
            );
        }
    }, [viewMode, selectedCategory]);

    useEffect(() => {
        const handleBack = () => {
            setViewMode('dashboard');
        };
        window.addEventListener('flashcards-back-action', handleBack);
        return () => {
            window.removeEventListener('flashcards-back-action', handleBack);
        };
    }, [viewMode]);

    // Persist starred ids in localStorage
    const toggleStar = (id: string, e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        setStarredIds(prev => {
            const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
            try {
                localStorage.setItem(`starred_flashcards_${certId || 'cna'}`, JSON.stringify(next));
            } catch (err) {
                console.warn(err);
            }
            return next;
        });
    };

    // Text to Speech logic
    const handleSpeak = (text: string, e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        if ('speechSynthesis' in window) {
            // Cancel current speaking
            window.speechSynthesis.cancel();
            const cleanText = text.replace(/[“""”]/g, '');
            const utterance = new SpeechSynthesisUtterance(cleanText);
            utterance.rate = 1.0;
            utterance.pitch = 1.0;
            window.speechSynthesis.speak(utterance);
        } else {
            console.warn("Speech synthesis not supported in this frame environment.");
        }
    };

    const handleResume = () => {
        if (user?.flashcardSession) {
            const sess = user.flashcardSession;
            setFilter(sess.filter as any);
            setSelectedCategory(sess.category);
            setCurrentIndex(Math.min(sess.currentIndex, cards.length - 1));
            setViewMode('study');
        }
        setShowResumeModal(false);
    };

    const handleStartFresh = async () => {
        setIsSyncing(true);
        try {
            await updateUser({ flashcardSession: null });
            setCurrentIndex(0);
            setMasteredInSession(0);
        } finally {
            setIsSyncing(false);
            setShowResumeModal(false);
        }
    };

    const handleSaveAndExit = async () => {
        if (isSessionComplete) {
            navigate(`/${certId || 'cna'}/dashboard`);
            return;
        }
        setIsSaving(true);
        try {
            const session: FlashcardSession = {
                certId: certId || 'cna',
                currentIndex,
                filter,
                category: selectedCategory,
                lastSaved: new Date().toISOString()
            };
            await updateUser({ flashcardSession: session });
            navigate(`/${certId || 'cna'}/dashboard`);
        } finally {
            setIsSaving(false);
        }
    };

    const handlePrint = () => {
        window.print();
        if (window.trackEvent) window.trackEvent('flashcard_registry_printed', 'flashcards', activeCert?.shortName || certId);
    };

    // Save progress periodically
    useEffect(() => {
        if (!isLoading && currentIndex > 0 && currentIndex % 5 === 0 && !showResumeModal && !isSessionComplete) {
            const session: FlashcardSession = {
                certId: certId || 'cna',
                currentIndex,
                filter,
                category: selectedCategory,
                lastSaved: new Date().toISOString()
            };
            updateUser({ flashcardSession: session });
        }
    }, [currentIndex]);

    // Categories with detailed telemetry counts
    const categoriesWithStats = useMemo(() => {
        const statsMap: Record<string, { total: number, mastered: number }> = {};
        
        cards.forEach(c => {
            if (!statsMap[c.category]) {
                statsMap[c.category] = { total: 0, mastered: 0 };
            }
            statsMap[c.category].total++;
            const meta = srsMap[c._id];
            if (meta && meta.level >= 5) {
                statsMap[c.category].mastered++;
            }
        });

        const sorted = Object.keys(statsMap).sort().map(cat => ({
            name: cat,
            total: statsMap[cat].total,
            mastered: statsMap[cat].mastered,
            percent: Math.round((statsMap[cat].mastered / statsMap[cat].total) * 100)
        }));

        // Insert All categories item
        const totalCards = cards.length;
        const totalMastered = Object.values(srsMap).filter((m: any) => m.level >= 5).length;
        const allPercent = Math.round((totalMastered / Math.max(1, totalCards)) * 100);

        return [
            { name: 'All', total: totalCards, mastered: totalMastered, percent: allPercent },
            ...sorted
        ];
    }, [cards, srsMap]);

    const allCategories = useMemo(() => {
        return categoriesWithStats.map(c => c.name);
    }, [categoriesWithStats]);

    // Compute active playing array
    const filteredCards = useMemo(() => {
        const now = new Date();
        return cards.filter(c => {
            const metadata = srsMap[c._id];
            const matchesSearch = c.front.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                 c.back.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = selectedCategory === 'All' || c.category === selectedCategory;
            const isStarred = starredIds.includes(c._id);
            
            if (!matchesSearch || !matchesCategory) return false;

            switch(filter) {
                case 'due': return !metadata || new Date(metadata.nextReview) <= now;
                case 'new': return !metadata;
                case 'mastered': return metadata && metadata.level >= 5;
                case 'starred': return isStarred;
                case 'review_later': return metadata && metadata.status === 'review';
                default: return true;
            }
        });
    }, [cards, filter, searchTerm, srsMap, selectedCategory, starredIds]);

    const srsStats = useMemo(() => {
        const now = new Date();
        const counts = { new: 0, learning: 0, review: 0, mastered: 0, reviewLater: 0 };
        cards.forEach(c => {
            const meta = srsMap[c._id];
            if (!meta) { counts.new++; } 
            else {
                if (meta.status === 'review') {
                    counts.reviewLater++;
                }
                if (meta.level >= 5) { counts.mastered++; } 
                else if (new Date(meta.nextReview) <= now) { counts.review++; } 
                else { counts.learning++; }
            }
        });
        return counts;
    }, [cards, srsMap]);

    const activeCard = filteredCards[currentIndex];

    const toggleFlip = () => {
        const nextState = !isFlipped;
        setIsFlipped(nextState);
        if (nextState && activeCard) {
            StorageService.trackUserEvent({
                action: 'flashcard_flip',
                category: 'flashcards',
                label: activeCard.term || activeCard.definition || 'Term',
                metadata: { flashcardId: activeCard._id, category: activeCard.category }
            });
        }
    };

    // Auto Play Slideshow Loop Helper
    useEffect(() => {
        let interval: any = null;
        if (isPlaying && filteredCards.length > 0 && !isSessionComplete) {
            interval = setInterval(() => {
                if (!isFlipped) {
                    setIsFlipped(true); // Flip first
                } else {
                    setIsFlipped(false); // Reset flip
                    // Move card
                    setCurrentIndex(prev => {
                        if (prev >= filteredCards.length - 1) {
                            setIsPlaying(false);
                            finishSession();
                            return prev;
                        } else {
                            return prev + 1;
                        }
                    });
                }
            }, autoplaySpeed);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isPlaying, isFlipped, autoplaySpeed, filteredCards.length, isSessionComplete]);

    // Real Interactive Keyboard Event Handlers
    useEffect(() => {
        const handleKeys = (e: KeyboardEvent) => {
            if (viewMode !== 'study' || isSessionComplete || studyType !== 'flashcards') return;
            
            // Do not fire shortcuts inside form fields
            const tag = e.target && (e.target as HTMLElement).tagName.toLowerCase();
            if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

            switch (e.code) {
                case 'Space':
                    e.preventDefault();
                    toggleFlip();
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    prevCard();
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    nextCard();
                    break;
                case 'KeyK': // K for mastered / locked
                    if (activeCard) {
                        e.preventDefault();
                        processAttempt(activeCard._id, 'know');
                    }
                    break;
                case 'KeyD': // D for Repeat / review soon
                    if (activeCard) {
                        e.preventDefault();
                        processAttempt(activeCard._id, 'dont_know');
                    }
                    break;
                case 'KeyR': // R for Review Interval
                    if (activeCard) {
                        e.preventDefault();
                        processAttempt(activeCard._id, 'review');
                    }
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeys);
        return () => window.removeEventListener('keydown', handleKeys);
    }, [viewMode, isSessionComplete, studyType, activeCard, filteredCards]);

    // Match Timer Logic
    useEffect(() => {
        let interval: any = null;
        if (matchTimerActive) {
            interval = setInterval(() => {
                setMatchTimer(prev => prev + 1);
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [matchTimerActive]);

    // Category style mapping
    const getCategoryStyles = (category: string) => {
        const low = category.toLowerCase();
        if (low.includes('infection')) return { color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' };
        if (low.includes('safety') || low.includes('emergency')) return { color: 'text-rose-500', bg: 'bg-rose-500/10', border: 'border-rose-500/20' };
        if (low.includes('vital') || low.includes('math')) return { color: 'text-sky-500', bg: 'bg-sky-500/10', border: 'border-sky-500/20' };
        if (low.includes('rights') || low.includes('legal')) return { color: 'text-purple-500', bg: 'bg-purple-500/10', border: 'border-purple-500/20' };
        if (low.includes('personal') || low.includes('care')) return { color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/20' };
        return { color: 'text-indigo-400', bg: 'bg-indigo-500/10', border: 'border-indigo-500/20' };
    };

    const getFlashcardCardBgClass = (category: string) => {
        if (tokens.isWarmAcademic) {
            return 'bg-white dark:bg-slate-900 border-2 border-[#E3DFD5] dark:border-slate-800 hover:border-[#0D6E6E] shadow-xs text-[#2D3748] dark:text-slate-100';
        }
        if (tokens.isClinical) {
            return 'bg-white dark:bg-slate-900 border-2 border-teal-100 dark:border-teal-900 hover:border-teal-600/50 shadow-sm text-slate-900 dark:text-slate-100';
        }
        if (tokens.isHighContrast) {
            return 'bg-white dark:bg-black border-2 border-black dark:border-amber-400 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] text-black dark:text-white';
        }
        if (tokens.isMinimal) {
            return 'bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 shadow-none text-slate-900 dark:text-slate-100';
        }
        if (tokens.isVibrant) {
            return 'bg-gradient-to-br from-violet-50/80 via-white to-indigo-50/50 dark:from-slate-900 dark:to-indigo-950/40 border border-violet-200/80 dark:border-violet-800/60 shadow-md';
        }

        const isVibrant = config?.enableVibrantCards !== false;
        if (!isVibrant) {
            return 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800';
        }
        
        const low = category.toLowerCase();
        if (low.includes('infection')) {
            return 'bg-gradient-to-br from-emerald-50/70 via-emerald-50/30 to-emerald-100/10 border-emerald-200/80 dark:from-slate-900/90 dark:via-emerald-950/35 dark:to-emerald-950/55 dark:border-emerald-800/60 shadow-[0_4px_20px_-4px_rgba(16,185,129,0.08)]';
        }
        if (low.includes('safety') || low.includes('emergency')) {
            return 'bg-gradient-to-br from-rose-50/70 via-rose-50/30 to-rose-100/10 border-rose-200/80 dark:from-slate-900/90 dark:via-rose-950/35 dark:to-rose-950/55 dark:border-rose-800/60 shadow-[0_4px_20px_-4px_rgba(244,63,94,0.08)]';
        }
        if (low.includes('vital') || low.includes('math')) {
            return 'bg-gradient-to-br from-sky-50/70 via-sky-50/30 to-sky-100/10 border-sky-200/80 dark:from-slate-900/90 dark:via-sky-950/35 dark:to-sky-950/55 dark:border-sky-800/60 shadow-[0_4px_20px_-4px_rgba(14,165,233,0.08)]';
        }
        if (low.includes('rights') || low.includes('legal')) {
            return 'bg-gradient-to-br from-purple-50/70 via-purple-50/30 to-purple-100/10 border-purple-200/80 dark:from-slate-900/90 dark:via-purple-950/35 dark:to-purple-950/55 dark:border-purple-800/60 shadow-[0_4px_20px_-4px_rgba(168,85,247,0.08)]';
        }
        if (low.includes('personal') || low.includes('care')) {
            return 'bg-gradient-to-br from-amber-50/70 via-amber-50/30 to-amber-100/10 border-amber-200/80 dark:from-slate-900/90 dark:via-amber-950/35 dark:to-amber-950/55 dark:border-amber-800/60 shadow-[0_4px_20px_-4px_rgba(245,158,11,0.08)]';
        }
        return 'bg-gradient-to-br from-indigo-50/70 via-indigo-50/30 to-indigo-100/10 border-indigo-200/80 dark:from-slate-900/90 dark:via-indigo-950/35 dark:to-indigo-950/55 dark:border-indigo-800/60 shadow-[0_4px_20px_-4px_rgba(99,102,241,0.08)]';
    };

    const srsLevelInfo = (id: string) => {
        const meta = srsMap[id];
        if (!meta) return { label: 'New Term', level: 0 };
        return { label: meta.level >= 5 ? 'Mastered' : `Review Level ${meta.level}`, level: meta.level };
    };

    const getNextReviewDate = (level: number, forceSoon: boolean = false) => {
        const date = new Date();
        if (forceSoon) {
            date.setHours(date.getHours() + 24);
            return date.toISOString();
        }
        const intervals = [0, 1, 3, 7, 14, 30]; 
        date.setDate(date.getDate() + (intervals[level] || 30));
        return date.toISOString();
    };

    const processAttempt = async (id: string, action: 'know' | 'dont_know' | 'review') => {
        if (isCelebrating || !activeCard) return;

        // Check & track guest exploration limit
        if (isGuest && config.isGuestTrialEnabled !== false && activeCard) {
            const { allowed, totalUsed, remaining } = GuestTrialService.trackFlashcardFlipped(activeCard._id, guestLimit);
            if (!allowed) {
                setShowGuestLimitModal(true);
                StorageService.trackUserEvent({
                    action: 'guest_limit_reached',
                    category: 'guest_trial',
                    label: `Limit reached (${totalUsed}/${guestLimit}) on flashcard`,
                    value: totalUsed,
                    metadata: {
                        limit: guestLimit,
                        totalExplored: totalUsed,
                        remaining,
                        cardId: activeCard._id
                    }
                });
            }
        }

        const current = srsMap[id] || { level: 0, nextReview: new Date().toISOString(), lastAttempt: new Date().toISOString() };
        let nextLevel = current.level;
        let forceSoon = false;

        if (action === 'know') {
            nextLevel = 5;
            setMasteredInSession(prev => prev + 1);
            setIsCelebrating(true);
            setTimeout(() => setIsCelebrating(false), 600);
            if (activeCard) {
                StorageService.trackUserEvent({
                    action: 'flashcard_flip',
                    category: 'flashcards',
                    label: activeCard.term || activeCard.definition || 'Term',
                    metadata: { flashcardId: activeCard._id, status: 'know', level: 5 }
                });
            }
        } else if (action === 'dont_know') {
            nextLevel = 0;
            forceSoon = false; 
            if (activeCard) {
                StorageService.trackUserEvent({
                    action: 'flashcard_fail',
                    category: 'flashcards',
                    label: activeCard.term || activeCard.definition || 'Term',
                    value: 1,
                    metadata: { flashcardId: activeCard._id, status: 'dont_know', level: 0 }
                });
            }
        } else if (action === 'review') {
            forceSoon = true;
            if (activeCard) {
                StorageService.trackUserEvent({
                    action: 'flashcard_flip',
                    category: 'flashcards',
                    label: activeCard.term || activeCard.definition || 'Term',
                    metadata: { flashcardId: activeCard._id, status: 'review' }
                });
            }
        }

        const nextMetadata: FlashcardSRSMetadata = {
            level: nextLevel,
            nextReview: getNextReviewDate(nextLevel, forceSoon),
            lastAttempt: new Date().toISOString(),
            status: action
        };

        const nextMap = { ...srsMap, [id]: nextMetadata };
        const xpReward = action === 'know' ? 100 : (action === 'review' ? 25 : 5);
        setSessionXp(prev => prev + xpReward);
        
        setIsSyncing(true);
        try {
            const updatedStats = GamificationService.addXP(user?.stats!, xpReward);
            const masteredCount = Object.values(nextMap).filter((m: any) => m.level >= 5).length;
            const newBadges = GamificationService.checkBadges(user, { 
                examsTaken: (user?.history || []).length, 
                lastScore: 0, level: updatedStats.level, emergencyScore: 0,
                flashcardsMastered: masteredCount 
            });

            await updateUser({ 
                flashcardSRS: nextMap, 
                stats: { ...updatedStats, badges: [...(updatedStats.badges || []), ...newBadges] } 
            });
        } catch (e) {
            console.error("Cloud Registry Sync Failure:", e);
        } finally {
            setIsSyncing(false);
        }

        if (action === 'know' || action === 'review') {
            setTimeout(nextCard, 400);
        } else {
            setIsFlipped(false);
            setShowHint(false);
        }
    };

    const nextCard = () => {
        // Check if guest user has exceeded exploration limit
        if (isGuest && config.isGuestTrialEnabled !== false && GuestTrialService.hasReachedLimit(guestLimit)) {
            setShowGuestLimitModal(true);
            return;
        }

        const nextIndex = currentIndex + 1;
        const interval = config.intervals.flashcards || 10;
        const rotationThreshold = config.intervals.adRotationThreshold || 10;

        if (nextIndex > 0 && nextIndex % rotationThreshold === 0) {
            setAdRefreshKey(prev => prev + 1);
        }

        if (user?.planTier === 'free' && config.isActive && config.intermissionEnabled && nextIndex > 0 && nextIndex % interval === 0 && lastAdIndex !== nextIndex && nextIndex < filteredCards.length) {
            setShowAdBreak(true);
            setLastAdIndex(nextIndex);
            return;
        }

        setIsFlipped(false);
        setShowHint(false);
        if (currentIndex >= filteredCards.length - 1) {
            finishSession();
        } else {
            setCurrentIndex(prev => prev + 1);
        }
    };

    const prevCard = () => {
        setIsFlipped(false);
        setShowHint(false);
        setCurrentIndex(prev => (prev === 0 ? filteredCards.length - 1 : prev - 1));
    };

    const finishSession = async () => {
        setIsSessionComplete(true);
        setIsPlaying(false);
        setIsSyncing(true);
        try {
            const newEntry = {
                id: `flash_${Date.now()}`,
                date: new Date().toLocaleDateString(),
                certId: certId || 'cna',
                examMode: 'Flashcard Lab',
                score: Math.round((masteredInSession / Math.max(1, filteredCards.length)) * 100),
                totalQuestions: filteredCards.length,
                passed: true,
                xpEarned: sessionXp
            };
            await updateUser({ history: [newEntry, ...(user?.history || [])].slice(0, 100), flashcardSession: null });
        } finally {
            setIsSyncing(false);
        }
    };

    // Begin interactive Study of dynamic set
    const beginStudySet = (catName: string) => {
        setSelectedCategory(catName);
        setCurrentIndex(0);
        setIsFlipped(false);
        setShowHint(false);
        setIsSessionComplete(false);
        setStudyType('flashcards');
        setViewMode('study');
    };

    // Match Practice Game Initialization
    const initMatchChallenge = () => {
        if (filteredCards.length < 2) return;
        
        // Take up to 4 dynamic cards
        const randomDeck = [...filteredCards].sort(() => 0.5 - Math.random()).slice(0, Math.min(4, filteredCards.length));
        const gridItems: MatchItem[] = [];
        
        randomDeck.forEach(c => {
            gridItems.push({ id: c._id, text: c.front, type: 'front' });
            gridItems.push({ id: c._id, text: c.back, type: 'back' });
        });

        // Fully shuffle placement grid
        setMatchPairs(gridItems.sort(() => 0.5 - Math.random()));
        setMatchedIds([]);
        setSelectedPairItem(null);
        setMatchTimer(0);
        setMatchTimerActive(true);
        setStudyType('match');
    };

    // Match click linking logic
    const handleMatchSelection = (item: MatchItem) => {
        if (matchedIds.includes(item.id)) return;

        // If no first item selected
        if (!selectedPairItem) {
            setSelectedPairItem(item);
            return;
        }

        // If clicking same column type, switch selection
        if (selectedPairItem.type === item.type) {
            setSelectedPairItem(item);
            return;
        }

        // Check matching card ID sequence
        if (selectedPairItem.id === item.id) {
            setMatchedIds(prev => [...prev, item.id]);
            setSelectedPairItem(null);
            
            // Award matching bonus XP!
            setSessionXp(prev => prev + 40);
            
            // Completed Set match?
            const totalPairsCount = Math.min(4, filteredCards.length);
            if (matchedIds.length + 1 === totalPairsCount) {
                setMatchTimerActive(false);
                // Check local high score
                const currentScore = matchTimer + 1;
                if (matchBestScore === 0 || currentScore < matchBestScore) {
                    setMatchBestScore(currentScore);
                    try {
                        localStorage.setItem(`match_best_time_${certId || 'cna'}`, currentScore.toString());
                    } catch (err) {
                        console.warn(err);
                    }
                }
                
                // Track points logic
                updateMatchXP(150);
            }
        } else {
            // Bad pair mismatch, reset selection quickly
            const first = selectedPairItem;
            setSelectedPairItem(item);
        }
    };

    const updateMatchXP = async (points: number) => {
        setIsSyncing(true);
        try {
            const updatedStats = GamificationService.addXP(user?.stats!, points);
            await updateUser({ stats: updatedStats });
        } catch {
            // failure
        } finally {
            setIsSyncing(false);
        }
    };

    const progressPercent = Math.round((srsStats.mastered / Math.max(1, cards.length)) * 100);

    if (showAdBreak) {
        return <AdIntervalBreak moduleName="Recall Lab" onComplete={() => setShowAdBreak(false)} />;
    }

    return (
        <div className="max-w-7xl mx-auto space-y-6 md:space-y-8 animate-fade-in pb-24 md:pb-32 text-slate-900 dark:text-slate-100 overflow-x-hidden">
            
            <ModuleHeader 
                icon={Zap}
                title="Study Flashcards"
                description="Practice with memory cards to learn and memorize words, terms, and tools quickly."
                badge="Memory Cards"
                color="indigo"
            />

            <AdUnit placement="flashcards_top" refreshKey={adRefreshKey} />

            {/* Guest Trial Free Limit Banner */}
            {isGuest && config.isGuestTrialEnabled !== false && (
                <div className="bg-amber-500/10 dark:bg-amber-500/20 border border-amber-500/30 rounded-2xl p-3 px-4 flex items-center justify-between gap-3 text-xs">
                    <div className="flex items-center gap-2">
                        <Sparkles size={16} className="text-amber-500 shrink-0" />
                        <span className="font-bold text-amber-900 dark:text-amber-200">
                            Free Exploration Mode: <strong className="font-black">{Math.min(guestState.totalItemsExplored, guestLimit)} / {guestLimit}</strong> items explored
                        </span>
                    </div>
                    <button
                        onClick={() => setShowGuestLimitModal(true)}
                        className="text-[11px] font-black uppercase tracking-wider text-amber-700 dark:text-amber-300 hover:underline shrink-0"
                    >
                        Create Free Account
                    </button>
                </div>
            )}

            {/* Print Friendly Registry Output */}
            <div id="flashcard-registry-print" className="hidden print:block w-full text-slate-950">
                <div className="print-page-break p-8">
                    <div className="flex justify-between items-center border-b-4 border-slate-900 pb-4 mb-10">
                        <h2 className="text-3xl font-black">{activeCert?.shortName} Recall Registry</h2>
                    </div>
                    <div className="grid grid-cols-2 gap-8">
                        {cards.map((c, i) => (
                            <div key={i} className="p-8 border-2 border-slate-200 rounded-3xl space-y-4 break-inside-avoid">
                                <span className="text-[10px] font-black text-slate-400">{c.category}</span>
                                <h3 className="text-lg font-bold">FRONT: {c.front}</h3>
                                <div className="h-px bg-slate-100"></div>
                                <p className="text-sm text-slate-600">BACK: {c.back}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Persistence state modal */}
            {showResumeModal && (
                <div className="fixed inset-0 z-[1000] bg-slate-950/80 backdrop-blur-xl flex items-start justify-center p-4 pt-10 md:pt-16 overflow-y-auto custom-scrollbar">
                    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] md:rounded-[3rem] p-8 md:p-14 max-w-xl w-full text-center space-y-8 animate-pop border border-slate-100 dark:border-slate-800 shadow-2xl">
                        <div className="w-16 h-16 md:w-24 md:h-24 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-[2rem] flex items-center justify-center mx-auto shadow-xl border border-indigo-100 dark:border-indigo-800">
                            <RotateCcw size={32} className="animate-spin-slow" />
                        </div>
                        <div className="space-y-3">
                            <h3 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900 dark:text-white">Resume Study Set?</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 font-bold leading-relaxed">
                                You have a saved session. Resume where you left off or initialize a new sequence.
                            </p>
                        </div>
                        <div className="flex flex-col gap-3">
                            <button onClick={handleResume} className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black text-xs tracking-widest shadow-xl active:scale-95 border-b-4 border-indigo-900 flex items-center justify-center gap-2 group">
                                <FastForward size={18} className="group-hover:translate-x-1 transition-transform" /> Resume study
                            </button>
                            <button onClick={handleStartFresh} className="w-full py-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-2xl font-black text-[10px] tracking-widest active:scale-95">Start Fresh run</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Quick action bar */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 no-print pt-2 px-1">
                <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-2xl shadow-xl shrink-0 ${tokens.isClinical ? 'bg-[#0F7D6C] text-white' : tokens.isHighContrast ? 'bg-amber-400 text-black border-2 border-black' : tokens.isMinimal ? 'bg-slate-800 text-white' : tokens.isVibrant ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white' : 'bg-indigo-600 text-white'}`}>
                        <Brain size={24} className="animate-pulse" />
                    </div>
                    <div>
                        <h2 className={`text-xl md:text-3xl font-black text-slate-900 dark:text-slate-50 tracking-tighter leading-none ${tokens.isClinical ? 'font-serif' : ''}`}>Recall Lab</h2>
                        <div className="flex items-center gap-2 mt-1.5">
                            <span className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[8px] font-black tracking-widest border shadow-sm ${isSyncing ? 'bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 border-indigo-100 dark:border-indigo-800' : 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 border-emerald-100 dark:border-emerald-800'}`}>
                                {isSyncing ? <Loader2 size={8} className="animate-spin" /> : <ShieldCheck size={8} />} 
                                {isSyncing ? 'Syncing...' : 'Registry Secure'}
                            </span>
                            <p className="text-[8px] font-bold text-slate-400 tracking-widest flex items-center gap-1"><Globe size={8}/> SRS Engine</p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2 w-full md:w-auto self-stretch md:self-auto font-bold">
                    {viewMode === 'study' && (
                        <button 
                            onClick={() => setViewMode('dashboard')} 
                            className="flex-1 md:flex-none px-4 py-2.5 bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white font-extrabold text-[10px] tracking-widest uppercase rounded-xl transition-all shadow-[0_4px_12px_rgba(244,63,94,0.3)] active:translate-y-px active:scale-95 group shrink-0 border border-rose-400/20 flex items-center justify-center gap-2"
                        >
                            Back to Sets
                        </button>
                    )}
                    <button onClick={handlePrint} className="flex-1 md:flex-none px-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl font-black text-[8px] tracking-widest text-slate-600 dark:text-slate-300 shadow-sm flex items-center justify-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-800">
                        <Printer size={12} /> <span className="hidden sm:inline">Print Decks</span>
                    </button>
                    <button onClick={handleSaveAndExit} disabled={isSaving} className="flex-1 md:flex-none px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl font-black text-[8px] tracking-widest text-white shadow-xl flex items-center justify-center gap-2 transition-all hover:bg-black active:scale-95">
                        {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save & Exit
                    </button>
                </div>
            </div>

            {/* SECTION 1: QUIZLET DECKS DASHBOARD */}
            {viewMode === 'dashboard' ? (
                <div className="space-y-8 no-print animate-fade-in">
                    
                    {/* Welcome Banner and Stats Overview */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
                        <div className={`md:col-span-2 p-6 md:p-8 rounded-[2rem] text-white flex flex-col justify-between shadow-2xl relative overflow-hidden group border ${
                          tokens.isWarmAcademic
                            ? 'bg-[#0D6E6E] border-[#0B5C5C] text-white'
                            : tokens.isClinical
                            ? 'bg-gradient-to-br from-[#0B2942] via-[#0D3C5C] to-[#071F33] border-teal-500/20'
                            : tokens.isHighContrast
                            ? 'bg-black text-amber-400 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
                            : tokens.isMinimal
                            ? 'bg-slate-900 border-slate-800'
                            : tokens.isVibrant
                            ? 'bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-950 border-violet-800'
                            : 'bg-gradient-to-br from-indigo-900 via-slate-900 to-slate-950 border-indigo-500/10'
                        }`}>
                            <div className="absolute top-0 right-0 p-10 opacity-10 pointer-events-none">
                                <BookOpen size={160} className="text-white transform rotate-12" />
                            </div>
                            <div className="relative z-10 space-y-2">
                                <span className="px-3 py-1 bg-white/10 rounded-full font-black text-[8px] tracking-widest uppercase text-teal-100">Mastery Overview</span>
                                <h3 className={`text-2xl md:text-3xl font-black tracking-tight leading-tight ${tokens.isClinical ? 'font-serif' : ''}`}>Master terms through scientific spacing</h3>
                                <p className="text-xs text-slate-100 dark:text-slate-200 font-medium max-w-sm leading-relaxed">
                                    Flashcards are ranked and reviewed automatically based on your mastery levels. Aim to lock every term into the top level.
                                </p>
                            </div>
                            <div className="relative z-10 pt-6 mt-4 border-t border-white/10 flex justify-between items-end">
                                <div>
                                    <p className={`text-base font-black leading-none ${
                                      tokens.isWarmAcademic ? 'text-amber-300' : tokens.isClinical ? 'text-teal-300' : tokens.isHighContrast ? 'text-amber-400' : tokens.isVibrant ? 'text-violet-300' : 'text-indigo-400'
                                    }`}>{progressPercent}%</p>
                                    <p className="text-[7.5px] font-black text-teal-100 tracking-wider uppercase mt-1">Locked Registry</p>
                                </div>
                                <button onClick={() => beginStudySet('All')} className={`px-5 py-2 rounded-xl font-black text-[8.5px] tracking-widest shadow-xl flex items-center gap-1.5 transition-all hover:translate-x-0.5 active:scale-95 ${
                                  tokens.isWarmAcademic
                                    ? 'bg-[#E05638] text-white hover:bg-[#C8472B]'
                                    : tokens.isClinical
                                    ? 'bg-[#0F7D6C] text-white hover:bg-[#0B6356]'
                                    : tokens.isHighContrast
                                    ? 'bg-amber-400 text-black border border-black'
                                    : tokens.isMinimal
                                    ? 'bg-slate-800 text-white'
                                    : tokens.isVibrant
                                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white'
                                    : 'bg-white text-slate-950'
                                }`}>
                                    Study All Decks <ArrowRight size={12} />
                                </button>
                            </div>
                        </div>

                        {/* Stats card */}
                        <div className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-slate-200 dark:border-slate-800 flex flex-col justify-between shadow-md">
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <h4 className="text-xs font-black text-slate-400 tracking-widest uppercase mb-1">XP Progression</h4>
                                    <Medal className="text-amber-500 shrink-0" size={18} />
                                </div>
                                <div className="space-y-1">
                                    <p className="text-3xl font-black text-slate-900 dark:text-white tabular-nums">+{sessionXp} XP</p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-bold">This study session</p>
                                </div>
                            </div>
                            <div className="pt-4 border-t border-slate-100 dark:border-slate-800/60">
                                <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${tokens.progressBar}`} style={{ width: `${Math.min(100, (sessionXp / 1000) * 100)}%` }}></div>
                                </div>
                                <div className="flex justify-between text-[8px] font-bold text-slate-400 mt-2">
                                    <span>Session Progress</span>
                                    <span>Goal: 1000 XP</span>
                                </div>
                            </div>
                        </div>

                        {/* Rapid Flash Recall Deck */}
                        <div className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-slate-200 dark:border-slate-800 flex flex-col justify-between shadow-md">
                            <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <h4 className="text-xs font-black text-slate-400 tracking-widest uppercase">Recall Due Cards</h4>
                                    <Clock className="text-rose-500 shrink-0" size={18} />
                                </div>
                                <div className="space-y-1">
                                    <p className="text-3xl font-black text-slate-900 dark:text-white tabular-nums">{srsStats.review}</p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-bold">Pending priority items</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => {
                                    setFilter('due');
                                    beginStudySet('All');
                                }}
                                disabled={srsStats.review === 0} 
                                className={`w-full py-3 disabled:opacity-40 rounded-xl font-black text-[8.5px] tracking-widest text-center shadow-lg transition-all active:scale-95 ${
                                  tokens.isClinical
                                    ? 'bg-[#0F7D6C] text-white hover:bg-[#0B6356]'
                                    : tokens.isHighContrast
                                    ? 'bg-amber-400 text-black border border-black'
                                    : tokens.isMinimal
                                    ? 'bg-slate-800 text-white'
                                    : tokens.isVibrant
                                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white'
                                    : 'bg-slate-900 dark:bg-slate-800 text-white hover:bg-black'
                                }`}
                            >
                                Start SRS Review
                            </button>
                        </div>
                    </div>

                    {/* Interactive Sets Search & Title */}
                    <div className="space-y-4 pt-2">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <div>
                                <h3 className={`text-lg md:text-xl font-black text-slate-900 dark:text-white flex items-center gap-2 ${tokens.isClinical ? 'font-serif' : ''}`}>
                                    <LayoutGrid size={20} className={tokens.accentText} /> Choose a Study Deck
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 font-bold">Select a clinical set to begin interactive flip cards, auto playlist, or matcher game.</p>
                            </div>
                            <div className="relative w-full md:w-80 group">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500" size={14} />
                                <input 
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search terms or categories..."
                                    className="w-full pl-9 pr-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl font-bold text-xs outline-none focus:ring-2 focus:ring-indigo-500/20"
                                />
                            </div>
                        </div>

                        {/* Grid list of Available Quizlet sets with mastery bars */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {categoriesWithStats.map((cat, idx) => {
                                const styles = getCategoryStyles(cat.name);
                                return (
                                    <div 
                                        key={idx}
                                        onClick={() => beginStudySet(cat.name)}
                                        className={`relative border rounded-[2.2rem] p-6 shadow-md hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-1 group flex flex-col justify-between h-56 overflow-hidden ${
                                            getFlashcardCardBgClass(cat.name)
                                        }`}
                                    >
                                        {/* Set card header */}
                                        <div className="space-y-3">
                                            <div className="flex justify-between items-center">
                                                <span className={`px-3 py-1 rounded-full font-black text-[7.5px] md:text-[8px] tracking-widest uppercase ${styles.bg} ${styles.color} ${styles.border}`}>
                                                    Module Deck
                                                </span>
                                                <span className="text-[10px] font-black text-slate-350 dark:text-slate-605 group-hover:text-indigo-500 transition-colors">
                                                    {cat.total} Terms
                                                </span>
                                            </div>
                                            <h4 className="text-lg font-black text-slate-950 dark:text-slate-50 tracking-tight leading-snug group-hover:text-indigo-500 transition-colors line-clamp-2">
                                                {cat.name}
                                            </h4>
                                        </div>

                                        {/* Progress slide bar */}
                                        <div className="space-y-2 pt-4 border-t border-slate-100 dark:border-slate-800/60 mt-auto">
                                            <div className="flex justify-between items-center text-[8.5px] font-black text-slate-400 tracking-wider">
                                                <span>Session Mastery</span>
                                                <span className="text-indigo-600 dark:text-indigo-400 font-bold tabular-nums">{cat.percent}%</span>
                                            </div>
                                            <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                <div 
                                                    className="h-full bg-indigo-500 rounded-full transition-all duration-700" 
                                                    style={{ width: `${cat.percent}%` }}
                                                />
                                            </div>
                                            <div className="flex justify-between items-center text-[7.5px] text-slate-350 dark:text-slate-505 pt-1">
                                                <span>{cat.mastered} Mastered</span>
                                                <span className="font-bold flex items-center gap-0.5 group-hover:translate-x-0.5 transition-transform">
                                                    Study Now <ChevronRight size={10} />
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            ) : (
                
                // SECTION 2: DYNAMIC STUDY WORKSPACE & DUAL PRACTICES
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 no-print animate-fade-in">
                    
                    {/* Secondary Navigation Right panel sidebar / stats */}
                    <div className="lg:col-span-3 space-y-4 order-2 lg:order-1">
                        <div className="bg-white dark:bg-slate-900 rounded-[2rem] p-5 md:p-6 border border-slate-200 dark:border-slate-800 shadow-sm space-y-5">
                            
                            {/* Current selected Set */}
                            <div className="space-y-1 pb-4 border-b border-slate-100 dark:border-slate-800/60">
                                <p className="text-[7.5px] font-black text-slate-400 tracking-widest uppercase">Target Folder Deck</p>
                                <h3 className="text-base font-black text-slate-900 dark:text-white line-clamp-1">{selectedCategory}</h3>
                                <p className="text-[8.5px] font-bold text-indigo-500">{filteredCards.length} Cards available</p>
                            </div>

                            {/* SRS Metrics Telemetry column */}
                            <div className="space-y-3">
                                <h4 className="text-[9px] font-black text-slate-400 tracking-widest uppercase flex items-center gap-1.5">
                                    <BarChart3 size={12} className="text-indigo-500" /> Deck Mastery
                                </h4>
                                <div className="space-y-2">
                                    <SRSMetric label="Locked" count={srsStats.mastered} color="bg-indigo-600" desc="Mastered" />
                                    <SRSMetric label="Review Later" count={srsStats.reviewLater} color="bg-cyan-500" desc="Tagged review later" />
                                    <SRSMetric label="Due Review" count={srsStats.review} color="bg-amber-500" desc="Spaced repetition" />
                                    <SRSMetric label="Learning" count={srsStats.learning} color="bg-emerald-500" desc="Confidence ranking" />
                                    <SRSMetric label="New Cards" count={srsStats.new} color="bg-slate-300 dark:bg-slate-700" desc="Unseen terms" />
                                </div>
                            </div>

                            {/* Session progress tracker bar */}
                            <div className="pt-3 border-t border-slate-100 dark:border-slate-800/60 space-y-2">
                                <div className="flex justify-between items-center text-[8px] font-black text-slate-400 tracking-wider">
                                    <span>XP Streak Tracker</span>
                                    <span className="text-indigo-600 dark:text-indigo-400">+{sessionXp} XP</span>
                                </div>
                                <div className="h-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                    <div className="h-full bg-indigo-500" style={{ width: `${Math.min(100, (sessionXp / 800) * 100)}%` }}></div>
                                </div>
                                <div className="p-3 bg-indigo-50 dark:bg-indigo-950/40 rounded-xl border border-indigo-100/40 dark:border-indigo-900/30 flex gap-2">
                                    <Sparkles size={12} className="text-indigo-500 shrink-0" />
                                    <p className="text-[8px] font-bold text-indigo-900 dark:text-indigo-300 leading-relaxed">
                                        "Locking items increases confidence rates by 12%."
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Mode Choice buttons */}
                        <div className="bg-white dark:bg-slate-900 p-4 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm space-y-2.5">
                            <h4 className="text-[8.5px] font-black text-slate-400 tracking-widest uppercase px-1">Interactive Modes</h4>
                            <button 
                                onClick={() => setStudyType('flashcards')}
                                className={`w-full flex items-center justify-between p-3 rounded-xl border text-[9.5px] font-black tracking-widest transition-all ${studyType === 'flashcards' ? tokens.activeTab : tokens.inactiveTab}`}
                            >
                                <span className="flex items-center gap-2"><BookOpen size={14} /> Flashcard Flip Mode</span>
                                <ChevronRight size={12} />
                            </button>
                            <button 
                                onClick={initMatchChallenge}
                                disabled={filteredCards.length < 2}
                                className={`w-full flex items-center justify-between p-3 rounded-xl border text-[9.5px] font-black tracking-widest transition-all disabled:opacity-40 ${studyType === 'match' ? tokens.activeTab : tokens.inactiveTab}`}
                            >
                                <span className="flex items-center gap-2"><Binary size={14} /> Quizlet Match Game</span>
                                <ChevronRight size={12} />
                            </button>
                        </div>
                    </div>

                    {/* Primary Interactive Board (Left) */}
                    <div className="lg:col-span-9 space-y-6 order-1 lg:order-2">
                        
                        {/* Session complete celebration frame */}
                        {isSessionComplete ? (
                            <div className="bg-slate-900 dark:bg-slate-950 rounded-[3rem] p-10 md:p-20 text-white text-center shadow-2xl relative overflow-hidden animate-pop border border-white/5">
                                <div className="absolute top-0 right-0 p-8 opacity-5"><Trophy size={140} /></div>
                                <div className="relative z-10 space-y-8">
                                    <div className="w-16 h-16 bg-emerald-500 rounded-2xl flex items-center justify-center mx-auto shadow-2xl rotate-3">
                                        <Trophy size={32} className="text-white" />
                                    </div>
                                    <div className="space-y-2">
                                        <h2 className="text-3xl md:text-5xl font-black tracking-tighter leading-none">Deck Session Cleared</h2>
                                        <p className="text-sm font-bold text-slate-400">Your spaced repetition parameters are logged.</p>
                                    </div>
                                    <div className="grid grid-cols-3 max-w-2xl mx-auto gap-4 py-6 border-y border-white/5">
                                        <div className="text-center">
                                            <p className="text-2xl md:text-3xl font-black tabular-nums">{Math.round((masteredInSession / Math.max(1, filteredCards.length)) * 100)}%</p>
                                            <p className="text-[8px] font-black text-slate-500 tracking-wider uppercase mt-1">Mastery Gain</p>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-2xl md:text-3xl font-black tabular-nums">{sessionXp}</p>
                                            <p className="text-[8px] font-black text-slate-500 tracking-wider uppercase mt-1">XP Earned</p>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-2xl md:text-3xl font-black tabular-nums">{filteredCards.length}</p>
                                            <p className="text-[8px] font-black text-slate-500 tracking-wider uppercase mt-1">Total Cards</p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col sm:flex-row justify-center gap-3 pt-4">
                                        <button onClick={() => { setIsSessionComplete(false); setCurrentIndex(0); setMasteredInSession(0); }} className="px-8 py-4 bg-indigo-600 text-white rounded-xl font-black text-[10px] tracking-widest shadow-xl active:scale-95 duration-100 hover:bg-indigo-500">Restart Session</button>
                                        <button onClick={() => setViewMode('dashboard')} className="px-8 py-4 bg-white text-slate-900 rounded-xl font-black text-[10px] tracking-widest shadow-xl active:scale-95 duration-100 hover:bg-slate-50">Back to Sets</button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* SRS Filtering Headers and Star Toggle */}
                                <div className="bg-white dark:bg-slate-900 rounded-[1.5rem] border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col md:flex-row md:items-center justify-between p-2 gap-2">
                                    <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar scroll-smooth">
                                        {(['all', 'due', 'new', 'mastered', 'starred', 'review_later'] as const).map(f => (
                                            <button 
                                                key={f} 
                                                onClick={() => {setFilter(f); setCurrentIndex(0);}} 
                                                className={`px-4 py-2 rounded-xl text-[9.5px] font-black tracking-widest transition-all whitespace-nowrap ${filter === f ? 'bg-slate-900 dark:bg-slate-800 text-white shadow-md' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'}`}
                                            >
                                                {f === 'due' ? 'Review Due' : f === 'starred' ? '⭐ Starred' : f === 'review_later' ? 'Review Later' : f}
                                            </button>
                                        ))}
                                    </div>

                                    {studyType === 'flashcards' && (
                                        <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 dark:bg-slate-800 rounded-lg shrink-0">
                                            {/* Auto play indicators */}
                                            <button 
                                                onClick={() => setIsPlaying(prev => !prev)} 
                                                className={`p-1.5 rounded-lg flex items-center justify-center gap-1 shrink-0 ${isPlaying ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-800'}`}
                                                title="Auto Play Slideshow"
                                            >
                                                {isPlaying ? <Pause size={12} /> : <Play size={12} />}
                                                <span className="text-[7.5px] font-black tracking-wider px-1">Auto Play</span>
                                            </button>
                                            
                                            {isPlaying && (
                                                <span className="text-[7.5px] font-bold text-indigo-500 tracking-wide animate-pulse inline-flex items-center gap-1">
                                                    <Timer size={10} className="animate-spin" /> {autoplaySpeed/1000}s loops
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* CORE WORKSPACE (SUB-WINDOW FOR EITHER FLASHCARDS OR MATCH GAME) */}
                                {studyType === 'flashcards' ? (
                                    
                                    // SUB-STUDY A: INTERACTIVE FLASHCARD FLIP
                                    <div className="space-y-6">
                                        {filteredCards.length > 0 ? (
                                            <>
                                                {/* True working flip card */}
                                                {!activeCard ? (
                                                    <PageLoader compact message="Loading Card..." />
                                                ) : (
                                                    <div className={`relative w-full max-w-3xl h-[420px] md:h-[500px] perspective-2000 mx-auto ${isCelebrating ? 'animate-celebrate' : ''}`}>
                                                        <div 
                                                            onClick={toggleFlip} 
                                                            className={`relative w-full h-full cursor-pointer transition-all duration-700 preserve-3d shadow-xl rounded-[2.5rem] md:rounded-[3rem] ${isFlipped ? 'rotate-y-180' : ''}`}
                                                        >
                                                            {/* FRONT OF THE FLASHCARD (CLEAN LIGHT DESIGN) */}
                                                            <div 
                                                                className={`absolute inset-0 backface-hidden rounded-[2.5rem] md:rounded-[3rem] p-8 md:p-14 border-b-[8px] flex flex-col justify-between shadow-lg overflow-hidden ring-1 ring-slate-100 dark:ring-indigo-900/10 z-10 ${
                                                                    getFlashcardCardBgClass(activeCard.category)
                                                                }`} 
                                                                style={{ transform: 'translateZ(1px)' }}
                                                            >
                                                                <div className="flex justify-between items-center mb-4 shrink-0">
                                                                    <div className={`px-3 py-1 rounded-lg text-[8px] font-black tracking-widest shadow-sm border ${getCategoryStyles(activeCard.category).bg} ${getCategoryStyles(activeCard.category).color} ${getCategoryStyles(activeCard.category).border}`}>
                                                                        {activeCard.category}
                                                                    </div>
                                                                    
                                                                    <div className="flex items-center gap-1.5">
                                                                        <button 
                                                                            onClick={(e) => toggleStar(activeCard._id, e)}
                                                                            className="p-1.5 hover:scale-110 transition-transform"
                                                                            title="Star Term"
                                                                        >
                                                                            <Star 
                                                                                size={18} 
                                                                                className={`transition-colors ${starredIds.includes(activeCard._id) ? 'text-amber-500 fill-amber-500' : 'text-slate-300 dark:text-slate-650'}`} 
                                                                            />
                                                                        </button>
                                                                        <button 
                                                                            onClick={(e) => handleSpeak(activeCard.front, e)}
                                                                            className="p-1.5 hover:scale-110 text-slate-300 hover:text-indigo-500 dark:text-slate-650 dark:hover:text-indigo-400 transition-all"
                                                                            title="Read Aloud"
                                                                        >
                                                                            <Volume2 size={18} />
                                                                        </button>
                                                                        <span className="text-[8px] font-black text-slate-400 tracking-wider">
                                                                            {srsLevelInfo(activeCard._id).label}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                <div className="flex-1 flex flex-col justify-center items-center text-center px-2 md:px-4 overflow-y-auto custom-scrollbar self-stretch my-2">
                                                                    <h3 className="text-xl md:text-3.5xl lg:text-4xl font-extrabold text-slate-950 dark:text-white tracking-tight leading-snug">
                                                                        "{activeCard.front}"
                                                                    </h3>

                                                                    {showHint && activeCard.mnemonic && (
                                                                        <div className="mt-4 p-4 bg-indigo-50/50 dark:bg-indigo-950/20 rounded-xl border border-dashed border-indigo-100 dark:border-indigo-900/40 animate-pop w-full max-w-md">
                                                                            <p className="text-[8px] font-black text-indigo-500 tracking-wider mb-1">Mnemonic Prompt</p>
                                                                            <p className="text-xs font-bold text-indigo-900 dark:text-indigo-200 leading-relaxed font-mono">
                                                                                "{activeCard.mnemonic}"
                                                                            </p>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                <div className="mt-4 flex flex-col items-center gap-3 shrink-0">
                                                                    {!isFlipped && activeCard.mnemonic && (
                                                                        <button 
                                                                            onClick={(e) => { e.stopPropagation(); setShowHint(!showHint); }} 
                                                                            className={`flex items-center gap-1.5 px-4 py-2 rounded-full border transition-all font-black text-xs tracking-wider ${showHint ? 'bg-indigo-600 border-indigo-700 text-white shadow-lg' : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 font-bold hover:border-indigo-500'}`}
                                                                        >
                                                                            <Lightbulb size={14} /> {showHint ? 'Hide Mnemonic' : 'Show Mnemonic'}
                                                                        </button>
                                                                    )}
                                                                    <button 
                                                                        onClick={(e) => { e.stopPropagation(); toggleFlip(); }}
                                                                        className={`px-5 py-2.5 rounded-full ${tokens.primaryBtn} text-white font-extrabold text-xs tracking-wider uppercase shadow-md hover:scale-105 active:scale-95 transition-all flex items-center gap-2`}
                                                                    >
                                                                        <RotateCcw size={14} /> Flip Card
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            {/* BACK OF THE FLASHCARD (DARK ENERGETIC DEFINITION CANVAS) */}
                                                            <div 
                                                                className={`absolute inset-0 backface-hidden ${tokens.heroBannerBg} rounded-[2.5rem] md:rounded-[3rem] p-6 md:p-12 border-b-[8px] ${tokens.accentBorder} shadow-2xl flex flex-col justify-between rotate-y-180 text-white overflow-hidden ring-1 ring-white/10 z-0`}
                                                            >
                                                                <div className="flex justify-between items-center mb-4 shrink-0">
                                                                    <div className="flex items-center gap-2">
                                                                        <Sparkles size={16} className="text-indigo-400" />
                                                                        <span className="text-[8px] font-black text-indigo-400 tracking-[0.2em] uppercase">Rationale Definition</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-1.5">
                                                                        <button 
                                                                            onClick={(e) => toggleStar(activeCard._id, e)}
                                                                            className="p-1 hover:scale-110"
                                                                        >
                                                                            <Star 
                                                                                size={18} 
                                                                                className={`transition-colors ${starredIds.includes(activeCard._id) ? 'text-amber-500 fill-amber-500' : 'text-slate-500'}`} 
                                                                            />
                                                                        </button>
                                                                        <button 
                                                                            onClick={(e) => handleSpeak(activeCard.back, e)}
                                                                            className="p-1 hover:scale-110 text-slate-500 hover:text-white transition-all"
                                                                            title="Read Aloud"
                                                                        >
                                                                            <Volume2 size={18} />
                                                                        </button>
                                                                        <Target size={18} className="text-white/20" />
                                                                    </div>
                                                                </div>

                                                                <div className="flex-1 overflow-y-auto custom-scrollbar my-2 pr-1 space-y-4">
                                                                    <h3 className="text-base md:text-2xl font-bold text-white tracking-wide leading-snug text-center">
                                                                        "{activeCard.back}"
                                                                    </h3>
                                                                    {activeCard.mnemonic && (
                                                                        <div className="w-full p-4 bg-white/5 rounded-2xl border border-white/10 shadow-inner">
                                                                            <p className="text-[8px] font-black text-indigo-400 tracking-[0.2em] mb-2 uppercase text-center">How to Remember</p>
                                                                            <p className="text-xs text-indigo-100 font-medium leading-relaxed text-center font-mono">
                                                                                "{activeCard.mnemonic}"
                                                                            </p>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Interactive recall grading choices */}
                                                                <div className="mt-4 space-y-2 shrink-0" onClick={e => e.stopPropagation()}>
                                                                    <div className="grid grid-cols-3 gap-2.5">
                                                                        <button 
                                                                            onClick={() => processAttempt(activeCard._id, 'dont_know')} 
                                                                            className="group flex flex-col items-center justify-center gap-1 py-2.5 px-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl transition-all active:scale-95 shadow-lg border-b-2 border-rose-800"
                                                                        >
                                                                            <XCircle size={18} className="text-white shrink-0" />
                                                                            <span className="text-[10px] md:text-[11px] font-extrabold tracking-wider uppercase text-white">Don't Know (D)</span>
                                                                        </button>
                                                                        <button 
                                                                            onClick={() => processAttempt(activeCard._id, 'review')} 
                                                                            className="group flex flex-col items-center justify-center gap-1 py-2.5 px-2 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-xl transition-all active:scale-95 shadow-lg border-b-2 border-amber-700"
                                                                        >
                                                                            <Clock size={18} className="text-slate-950 shrink-0" />
                                                                            <span className="text-[10px] md:text-[11px] font-extrabold tracking-wider uppercase text-slate-950">Review Later (R)</span>
                                                                        </button>
                                                                        <button 
                                                                            onClick={() => processAttempt(activeCard._id, 'know')} 
                                                                            className="group flex flex-col items-center justify-center gap-1 py-2.5 px-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl transition-all active:scale-95 shadow-lg border-b-2 border-emerald-800"
                                                                        >
                                                                            <CheckCircle size={18} className="text-white shrink-0" />
                                                                            <span className="text-[10px] md:text-[11px] font-extrabold tracking-wider uppercase text-white">Know (K)</span>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Swipe & Selector bar */}
                                                <div className="flex flex-col items-center gap-4 w-full max-w-xl mx-auto pt-4">
                                                    <div className="flex items-center justify-between w-full gap-4 px-2">
                                                        <button onClick={prevCard} className={`p-4 ${tokens.secondaryBtn} rounded-2xl transition-all active:scale-90 shadow-md`}>
                                                            <ChevronLeft size={24} />
                                                        </button>
                                                        
                                                        {/* Quizlet smooth slide counter with progress percentage */}
                                                        <div className={`flex-1 px-5 py-4 ${tokens.heroBannerBg} rounded-2xl text-center shadow-lg border border-white/5 relative overflow-hidden group`}>
                                                            <div className={`absolute top-0 left-0 h-1 ${tokens.progressBar} shadow-[0_0_10px_currentColor] transition-all duration-300`} style={{ width: `${Math.round(((currentIndex + 1) / filteredCards.length) * 100)}%` }}></div>
                                                            <p className="text-xl font-black tabular-nums tracking-tighter leading-none">
                                                                {currentIndex + 1} <span className="opacity-20 mx-1">/</span> {filteredCards.length}
                                                            </p>
                                                            <p className="text-[8px] font-black tracking-widest opacity-60 uppercase mt-1.5">Progress Loop</p>
                                                        </div>

                                                        <button onClick={nextCard} className={`p-4 ${tokens.primaryBtn} rounded-2xl transition-all active:scale-90 shadow-md`}>
                                                            <ChevronRight size={24} />
                                                        </button>
                                                    </div>
                                                    
                                                    {/* Keyboard layout indicators */}
                                                    <div className="hidden sm:flex px-6 py-3 bg-slate-50 dark:bg-slate-900 text-slate-400 dark:text-slate-505 rounded-full text-[9px] font-black tracking-widest border border-slate-250 dark:border-slate-800 items-center justify-center gap-3.5 shadow-inner">
                                                        <Keyboard size={14} className="text-indigo-500" /> 
                                                        <span>SPACE: FLIP • LEFT/RIGHT ARROWS: NAVIGATE • K: KNOW • R: REVIEW LATER • D: DON'T KNOW</span>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="max-w-md mx-auto py-20 text-center space-y-6 animate-pop px-6">
                                                <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-slate-200 dark:border-slate-700 shadow-inner">
                                                    <Database size={30} className="text-slate-400" />
                                                </div>
                                                <div className="space-y-1">
                                                    <h3 className="text-lg font-black text-slate-950 dark:text-white">Registry Cards Empty</h3>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-bold leading-relaxed">
                                                        "No clinical term nodes matches your current study filter or star state in this category."
                                                    </p>
                                                </div>
                                                <button 
                                                    onClick={() => {setFilter('all'); setStarredIds([]); setSearchTerm(''); setIsSessionComplete(false);}} 
                                                    className="w-full py-3.5 bg-slate-900 dark:bg-slate-800 text-white rounded-xl font-black text-[9px] tracking-widest shadow-xl border-b-2 border-slate-800 active:scale-95 transition-all"
                                                >
                                                    Clear Selection Filters
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    
                                    // SUB-STUDY B: INTERACTIVE DYNAMIC MATCH GAME
                                    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-6 md:p-10 border border-slate-200 dark:border-slate-800 shadow-xl space-y-8 min-h-[460px] flex flex-col justify-between">
                                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-4 border-b border-slate-100 dark:border-slate-800/60 pb-4">
                                            <div className="space-y-1">
                                                <h3 className="text-lg font-black text-slate-950 dark:text-white flex items-center gap-1.5 uppercase tracking-tight">
                                                    <Binary size={18} className="text-indigo-600" /> Quizlet Time Matcher
                                                </h3>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 font-bold">Match definitions to terms as quickly as possible. Clear the board to gain massive bonus XP!</p>
                                            </div>
                                            
                                            {/* Live timer count feedback */}
                                            <div className="flex items-center gap-4">
                                                <div className="px-4 py-2 bg-slate-900 text-white rounded-lg font-mono font-black text-sm tracking-widest uppercase">
                                                    Timer: {matchTimer}s
                                                </div>
                                                {matchBestScore > 0 && (
                                                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                                                        🏆 Best Record: <span className="text-amber-500">{matchBestScore}s</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Match board grids */}
                                        {matchedIds.length === Math.min(4, filteredCards.length) ? (
                                            <div className="text-center py-10 space-y-6">
                                                <div className="w-12 h-12 bg-emerald-500 text-white rounded-full flex items-center justify-center mx-auto shadow-lg animate-bounce">
                                                    <Check size={24} />
                                                </div>
                                                <div className="space-y-1">
                                                    <h3 className="text-2xl font-black text-slate-950 dark:text-white">Board cleared in {matchTimer} seconds!</h3>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-bold">+150 Match Mastery XP unlocked.</p>
                                                </div>
                                                <div className="flex justify-center gap-3">
                                                    <button onClick={initMatchChallenge} className="px-8 py-3.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-black text-[9px] tracking-widest uppercase active:scale-95 transition-all">Match Another Deck</button>
                                                    <button onClick={() => setStudyType('flashcards')} className="px-8 py-3.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-300 rounded-xl font-black text-[9px] tracking-widest uppercase active:scale-95 transition-all">Return to Flip Deck</button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4">
                                                {matchPairs.map((item, keyIdx) => {
                                                    const isMatched = matchedIds.includes(item.id);
                                                    const isSelected = selectedPairItem && selectedPairItem.id === item.id && selectedPairItem.type === item.type;
                                                    
                                                    return (
                                                        <div 
                                                            key={keyIdx}
                                                            onClick={() => handleMatchSelection(item)}
                                                            className={`h-28 flex items-center justify-center text-center p-4 rounded-xl border-2 cursor-pointer transition-all active:scale-95 overflow-y-auto custom-scrollbar leading-tight font-bold text-xs select-none ${isMatched ? 'bg-emerald-500-10 border-emerald-500 text-emerald-500 pointer-events-none opacity-40 scale-95' : isSelected ? 'bg-indigo-600 border-indigo-700 text-white shadow-lg rotate-1 scale-102' : 'bg-slate-50 dark:bg-slate-850 border-slate-150 dark:border-slate-800 text-slate-950 dark:text-slate-100 hover:border-indigo-300'}`}
                                                        >
                                                            <p className="text-[10.5px] leading-relaxed font-black">{item.text}</p>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        <div className="flex justify-between items-center bg-slate-50 dark:bg-slate-950 p-4 rounded-xl border border-slate-200 dark:border-slate-850">
                                            <span className="text-[9px] text-slate-400 font-bold tracking-wider flex items-center gap-1.5">
                                                <Info size={12} className="text-indigo-500" /> Tap a card front on one column then select its rationale card on another.
                                            </span>
                                            <button onClick={initMatchChallenge} className="text-[9px] text-indigo-500 hover:text-indigo-600 font-extrabold uppercase tracking-widest flex items-center gap-1">
                                                <RefreshCw size={10} /> Reset Grid
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* SECTION 3: TERMS LIST IN THIS DECK ("Terms in this Set") */}
                                <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-805 shadow-md p-6 md:p-8 space-y-6 no-print pt-6">
                                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-100 dark:border-slate-800/60 pb-4">
                                        <div className="space-y-1">
                                            <h3 className="text-xl font-black text-slate-950 dark:text-slate-50 tracking-tight flex items-center gap-2">
                                                <ListFilter size={18} className="text-indigo-600" /> Deck Catalog: Terms in this Set ({filteredCards.length})
                                            </h3>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 font-bold">Browse registry glossary definitions. Click any terms to load active card.</p>
                                        </div>
                                    </div>

                                    {/* Catalog rows layout */}
                                    {filteredCards.length > 0 ? (
                                        <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-[350px] overflow-y-auto custom-scrollbar pr-2 space-y-4 pt-2">
                                            {filteredCards.map((c, i) => (
                                                <div 
                                                    key={c._id}
                                                    onClick={() => {
                                                        setCurrentIndex(i);
                                                        setIsFlipped(false);
                                                        setStudyType('flashcards');
                                                        window.scrollTo({ top: 300, behavior: 'smooth' });
                                                    }}
                                                    className={`group flex flex-col md:flex-row justify-between items-start md:items-center py-4 px-2 hover:bg-slate-50 dark:hover:bg-slate-850 rounded-xl transition-all cursor-pointer border ${i === currentIndex && studyType === 'flashcards' ? 'border-indigo-400 bg-indigo-500/5 dark:bg-indigo-950/10' : 'border-transparent'}`}
                                                >
                                                    <div className="space-y-2 flex-1 pr-6">
                                                        <div className="flex items-center gap-2.5">
                                                            <span className="w-5 h-5 rounded-full bg-slate-100 dark:bg-slate-800 text-[10px] font-black text-slate-400 flex items-center justify-center shrink-0">
                                                                {i + 1}
                                                            </span>
                                                            <h5 className="text-base font-black text-slate-950 dark:text-white leading-snug">
                                                                {c.front}
                                                            </h5>
                                                        </div>
                                                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed pl-7">
                                                            {c.back}
                                                        </p>
                                                    </div>

                                                    <div className="flex items-center gap-3 shrink-0 mt-3 md:mt-0 pl-7 md:pl-0" onClick={e => e.stopPropagation()}>
                                                        {c.mnemonic && (
                                                            <span className="text-[10px] font-mono text-indigo-500 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-1 rounded">
                                                                💡 Has mnemonic
                                                            </span>
                                                        )}
                                                        <button 
                                                            onClick={(e) => handleSpeak(c.front + " is defined as " + c.back, e)}
                                                            className="p-2 bg-slate-50 dark:bg-slate-800/60 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 hover:text-indigo-600 border border-slate-100 dark:border-slate-800 rounded-lg text-slate-400 transition-all active:scale-95"
                                                            title="Read Term & Definition"
                                                        >
                                                            <Volume2 size={14} />
                                                        </button>
                                                        <button 
                                                            onClick={(e) => toggleStar(c._id, e)}
                                                            className="p-2 bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-100 dark:border-slate-800 rounded-lg text-slate-400 transition-all active:scale-95"
                                                            title="Star Term"
                                                        >
                                                            <Star 
                                                                size={14} 
                                                                className={starredIds.includes(c._id) ? 'text-amber-500 fill-amber-500' : 'text-slate-350'} 
                                                            />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="py-8 text-center text-slate-400 text-xs font-bold font-mono">
                                            No terms indexed under this selection.
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Guest Exploration Limit Conversion Modal */}
            <GuestLimitModal 
                isOpen={showGuestLimitModal}
                onClose={() => setShowGuestLimitModal(false)}
                limit={guestLimit}
                totalExplored={guestState.totalItemsExplored}
                featureName="Flashcards"
            />

            <style>{`
                .perspective-2000 { perspective: 2000px; }
                .preserve-3d { transform-style: preserve-3d; }
                .backface-hidden { backface-visibility: hidden !important; -webkit-backface-visibility: hidden !important; }
                .rotate-y-180 { transform: rotateY(180deg); }
                @keyframes celebrate { 0% { transform: scale(1); } 50% { transform: scale(1.05) rotate(0.5deg); } 100% { transform: scale(1); } }
                .animate-celebrate { animation: celebrate 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
                
                /* Subtle horizontal scrollbar */
                .custom-scrollbar-horizontal::-webkit-scrollbar { height: 4px; }
                .custom-scrollbar-horizontal::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar-horizontal::-webkit-scrollbar-thumb { background: #1b263b; border-radius: 10px; }

                @media print {
                    @page { size: letter; margin: 0; }
                    body * { visibility: hidden !important; }
                    #flashcard-registry-print, #flashcard-registry-print * { visibility: visible !important; display: block !important; }
                    #flashcard-registry-print { position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; background: white !important; }
                    .print-page-break { page-break-after: always !important; height: 100% !important; display: flex !important; flex-direction: column !important; padding: 1.5cm !important; box-sizing: border-box !important; background: white !important; }
                    .no-print { display: none !important; }
                }
            `}</style>
        </div>
    );
};

const SRSMetric = ({ label, count, color, desc }: { label: string, count: number, color: string, desc: string }) => (
    <div className="flex items-center justify-between p-3 bg-slate-50/50 dark:bg-slate-800/40 rounded-xl border border-slate-150 dark:border-slate-800 group transition-all hover:bg-white dark:hover:bg-slate-800 hover:border-indigo-100 hover:shadow-md">
        <div className="flex items-center gap-2.5">
            <div className={`w-2 h-2 rounded-full ${color} shadow-sm group-hover:scale-125 transition-transform`}></div>
            <div>
                <p className="text-[10px] font-black text-slate-800 dark:text-slate-200 tracking-tight leading-none">{label}</p>
                <p className="text-[7.5px] text-slate-400 mt-1 tracking-wider">{desc}</p>
            </div>
        </div>
        <span className="text-xs font-black text-slate-900 dark:text-slate-100 tabular-nums">{count}</span>
    </div>
);
